/**
 * @file RevenueByProductReport.tsx
 * @brief Super Admin report — revenue / cost / profit by billable product.
 *
 * @references
 * - GET /api/admin/reports/revenue-by-product
 * - docs/product/reporting.md (sa-revenue-product)
 *
 * @author DES
 * @date 27-07-2026
 */

import { useMemo, useState } from "react";
import { Coins, DollarSign, FileText, TrendingUp } from "lucide-react";
import { StatCard } from "@/features/analytics/components/StatCard";
import type { AnalyticsRange as RangeBarValue } from "@/features/analytics/components/AnalyticsRangeBar";
import { ProductLabelCell } from "@/features/reports-shared/productIcons";
import {
  ReportKpiCell,
  ReportKpiRow,
  ReportSectionHeader,
  ReportTableCell,
} from "@/features/reports-shared/ReportSection";
import { formatMoneyMinor } from "@/shared/utils/formatters";
import { groupRowsByCurrency } from "@/shared/utils/reportCurrency";
import { percentChange } from "@/features/dashboard/kpiCompare";
import {
  previousRange,
  previousRangeLabel,
  sumInCurrency,
} from "@/features/reports-shared/previousRange";
import { useRevenueByProductReport } from "../api";
import { ReportShell } from "./ReportShell";
import { ReportTable } from "./ReportTable";

function defaultRange(): RangeBarValue {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
    groupBy: "day",
  };
}

export function RevenueByProductReport() {
  const [range, setRange] = useState<RangeBarValue>(() => defaultRange());
  const [companyId, setCompanyId] = useState<string | null>(null);

  const queryRange = useMemo(
    () => ({
      startDate: range.startDate,
      endDate: range.endDate,
      companyId: companyId ?? undefined,
    }),
    [range.startDate, range.endDate, companyId],
  );

  const report = useRevenueByProductReport(queryRange);
  // The same-length window before, for the +/-% pills (03-10-2026).
  const prevQueryRange = useMemo(
    () => ({ ...queryRange, ...previousRange(queryRange) }),
    [queryRange],
  );
  const prevRows = useRevenueByProductReport(prevQueryRange).data;
  const compareLabel = previousRangeLabel(queryRange);
  // Memoized so the currency grouping below is not rebuilt on every
  // render (the `?? []` literal is a fresh array each time).
  const rows = useMemo(() => report.data ?? [], [report.data]);
  // (24-08-2026) This feed is PLATFORM-wide, so it spans tenants and therefore
  // currencies. Rows now arrive per (product, currency) and the totals below
  // used to add their minor units together and print the result as "GBP" —
  // wrong, not merely mislabelled, the moment one non-GBP company exists.
  // Revenue/cost/profit stay here: this is the super admin's own P&L view.
  const byCurrency = useMemo(
    () => groupRowsByCurrency(rows, (r) => Number(r.revenue_cents || 0)),
    [rows],
  );
  const visibleRows = byCurrency.primaryRows;
  const currency = byCurrency.primary;


  const totals = useMemo(
    () =>
      visibleRows.reduce(
        (acc, row) => ({
          invoice_count: acc.invoice_count + Number(row.invoice_count || 0),
          revenue_cents: acc.revenue_cents + Number(row.revenue_cents || 0),
          cost_cents: acc.cost_cents + Number(row.cost_cents || 0),
          profit_cents: acc.profit_cents + Number(row.profit_cents || 0),
        }),
        {
          invoice_count: 0,
          revenue_cents: 0,
          cost_cents: 0,
          profit_cents: 0,
        },
      ),
    [visibleRows],
  );

  const prevDelta = (now: number, pick: (r: NonNullable<typeof prevRows>[number]) => unknown) =>
    prevRows ? percentChange(now, sumInCurrency(prevRows, currency, (r) => Number(pick(r) || 0))) : undefined;

  return (
    <ReportShell
      title="Revenue by product"
      description="Billable products aligned with the unified invoice feed. Shown in the platform's largest currency."
      range={range}
      onRangeChange={setRange}
      showCompanyFilter
      companyId={companyId}
      onCompanyChange={setCompanyId}
      exportDisabled={visibleRows.length === 0}
      serverExport={{
        scope: "admin",
        reportKey: "revenue-by-product",
        params: {
          startDate: range.startDate,
          endDate: range.endDate,
          companyId: companyId ?? undefined,
        },
      }}
      enablePdf
    >
      <>
        <ReportKpiRow>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Revenue"
              icon={DollarSign}
              value={formatMoneyMinor(totals.revenue_cents, currency)}
              delta={prevDelta(totals.revenue_cents, (r) => r.revenue_cents)}
              hint={compareLabel}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Cost"
              icon={Coins}
              value={formatMoneyMinor(totals.cost_cents, currency)}
              delta={prevDelta(totals.cost_cents, (r) => r.cost_cents)}
              deltaGoodWhen="down"
              hint={compareLabel}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Profit"
              icon={TrendingUp}
              value={formatMoneyMinor(totals.profit_cents, currency)}
              delta={prevDelta(totals.profit_cents, (r) => r.profit_cents)}
              hint={compareLabel}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Invoices"
              icon={FileText}
              value={String(totals.invoice_count)}
              delta={prevDelta(totals.invoice_count, (r) => r.invoice_count)}
              hint={compareLabel}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
        </ReportKpiRow>

        <ReportSectionHeader
          title="Product breakdown"
          description="Revenue uses charged amounts; cost is product COGS where tracked (Plan seats have no AI cost in this report)."
        />
        <ReportTableCell>
          <ReportTable
            headers={[
              "Product",
              { label: "Invoices", align: "right" },
              { label: "Revenue", align: "right" },
              { label: "Cost", align: "right" },
              { label: "Profit", align: "right" },
            ]}
            isLoading={report.isLoading}
            empty={visibleRows.length === 0}
            footer={
              visibleRows.length > 0 ? (
                <tr>
                  <td className="px-4 py-2">Total</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {totals.invoice_count}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatMoneyMinor(totals.revenue_cents, currency)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatMoneyMinor(totals.cost_cents, currency)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatMoneyMinor(totals.profit_cents, currency)}
                  </td>
                </tr>
              ) : null
            }
          >
            {visibleRows.map((row) => (
              <tr key={row.product} className="hover:bg-secondary/20">
                <td className="px-4 py-2">
                  <ProductLabelCell
                    product={row.product}
                    label={row.product_label}
                  />
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {row.invoice_count}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {formatMoneyMinor(row.revenue_cents, currency)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {formatMoneyMinor(row.cost_cents, currency)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {formatMoneyMinor(row.profit_cents, currency)}
                </td>
              </tr>
            ))}
          </ReportTable>
        </ReportTableCell>
      </>
    </ReportShell>
  );
}
